import { HttpHeaders, HttpUrlEncodingCodec } from '@angular/common/http';
import { CustomHttpUrlEncodingCodec } from './custom-http-url-encoding-codec';

export interface ApiConfigOptions {
  apiKeys?: { [key: string]: string };
  username?: string;
  password?: string;
  accessToken?: string | (() => string);
  basePath?: string;
  withCredentials?: boolean;

  /**
   * Factory for encoding codec used to parse and serialize query parameters (Using HttpParams).
   * When not set, defaults to a factory for {@link CustomHttpUrlEncodingCodec}
   */
  encoderFactory?: (() => HttpUrlEncodingCodec) | undefined | null | false;

  defaultHeaders?: HttpHeaders;
}

export class Configuration {
  readonly apiKeys: { [key: string]: string };
  username?: string;
  password?: string;
  accessToken?: string | (() => string);
  basePath?: string;
  withCredentials: boolean;

  /**
   * Factory for encoding codec used to parse and serialize query parameters (Using HttpParams).
   * When not set, defaults to a factory for {@link CustomHttpUrlEncodingCodec}
   */
  encoderFactory?: () => HttpUrlEncodingCodec;

  defaultHeaders?: HttpHeaders;

  constructor(configurationParameters: ApiConfigOptions = {}) {
    this.apiKeys = configurationParameters.apiKeys || {};
    this.username = configurationParameters.username;
    this.password = configurationParameters.password;
    this.accessToken = configurationParameters.accessToken;
    this.basePath = configurationParameters.basePath;

    this.withCredentials = !!configurationParameters.withCredentials;

    if (configurationParameters.hasOwnProperty('defaultHeaders')) {
      this.defaultHeaders = configurationParameters.defaultHeaders;
    } else {
      this.defaultHeaders = new HttpHeaders();
    }

    if (configurationParameters.hasOwnProperty('encoderFactory')) {
      if (typeof configurationParameters.encoderFactory === 'function') {
        this.encoderFactory = configurationParameters.encoderFactory;
      }
    } else {
      this.encoderFactory = () => new CustomHttpUrlEncodingCodec();
    }
  }

  /**
   * Set a default header.
   * When value is not supplied (undefined) the header is deleted.
   */
  setDefaultHeader(name: string, value?: string | string[]): void {
    if (value === undefined) {
      this.defaultHeaders = this.defaultHeaders.delete(name);
    } else {
      this.defaultHeaders = this.defaultHeaders.set(name, value);
    }
  }

  setApiKey(name: string, value?: any): void {
    if (value === undefined) {
      delete this.apiKeys[name];
    } else {
      this.apiKeys[name] = value;
    }
  }

  /**
   * Configures the object for OAuth2.
   * Use true on `setKey` when the schema does not specify "oauth2" security type but you want to emulate it.
   */
  setOauth2Credentials(token: string, setKey?: boolean): void {
    this.accessToken = token;
    this.setApiKey('Authorization', token ? 'Bearer ' + token : token);
  }

  /**
   * Select the correct content-type to use for a request.
   * Uses {@link Configuration#isJsonMime} to determine the correct content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param contentTypes - the array of content types that are available for selection
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderContentType(contentTypes: string[]): string | undefined {
    return this.selectHeaderAccept(contentTypes);
  }

  /**
   * Select the correct accept content-type to use for a request.
   * Uses {@link Configuration#isJsonMime} to determine the correct accept content-type.
   * If no content type is found return the first found type if the contentTypes is not empty
   * @param accepts - the array of content types that are available for selection.
   * @returns the selected content-type or <code>undefined</code> if no selection could be made.
   */
  selectHeaderAccept(accepts: string[]): string | undefined {
    if (accepts.length == 0) {
      return undefined;
    }

    let type: string;
    for (let mime of accepts) {
      if (mime === 'application/json') {
        return mime;
      } else if (this.isJsonMime(mime)) {
        type = mime;
      }
    }

    if (type === undefined) {
      return accepts[0];
    }
    return type;
  }

  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime: string): boolean {
    const jsonMime: RegExp = new RegExp('^([^;/ \t]+/[^;/ \t]+[+]json)[ \t]*(;.*)?$', 'i');
    return mime != null && (jsonMime.test(mime) || mime.toLowerCase() === 'application/json-patch+json');
  }
}
